import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { useIntl } from 'react-intl';
import Panel from '@tovia/man-ui/lib/components/Panel/Panel';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormErrors, SubmissionError } from 'redux-form';
import { useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import { PurchaseButton } from 'src/client/components/buttons/PurchaseButton';
import { PurchaseGalleryDialog } from '../PurchaseGallery/PurchaseGalleryDialog';
import { ApiClient } from 'src/client/helpers';
import urls, { constructUrl } from 'src/shared/urls';
import { purchase as cbpPurchase } from 'src/client/redux/modules/PPSInfo';
import { useSelector } from 'src/client/redux';
import { BillerById } from 'src/shared/constants/billers';
import { addDashesToUUID, generatePurchaseProduct } from 'src/shared/converters';
import { Gallery } from 'src/@types/Gallery';
import { MAM_COOKIE_NAME } from 'src/shared/constants/cookies';
import { ResponseError } from 'superagent';
import { useGetGalleryCoverUrl } from 'src/client/helpers/urls/useGetGalleryCoverUrl';

const PanelWrapper = styled.div`
  text-align: center;

  & .headline {
    font-size: 0.875rem;

    @media (min-width: 992px) and (max-width: 1199px) {
      font-size: 0.625rem;
    }
  }

  & .btn {
    font-weight: bold;
    position: relative;
    text-align: center;
    margin: 5px auto;
    padding: 10px;
    font-size: 0.875rem;
    transition: opacity 0.3s;

    :hover {
      opacity: 0.9;
    }
  }

  & .icon {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    text-shadow: none;
  }

  & .btn.btn-download {
    width: 50%;
    min-width: 220px;
    padding-right: 12px;
    border-width: 1px;
    border-style: solid;
    background: #f9d083;
    color: #000;
    border-color: #be8c31;
    max-width: 250px;

    .icon {
      padding: 0 12px;
      line-height: 40px !important;
      background: rgba(255, 255, 255, 0.3);
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      text-shadow: none;
    }

    .text {
      padding-left: 35px;

      + span {
        float: right;
        margin-top: 7px;
      }
    }
  }

  & .btn .caret {
    margin-left: 8px;
  }

  .footer {
    color: ${(props) => props.theme.primary7};
  }

  & .purchase-modal {
    @media (max-width: 767px) {
      height: 100%;
      width: 100%;
    }
  }

  & .purchase-modal-body {
    padding: 20px 5px;
  }

  & .no-dropdown {
    margin-left: 33px;
  }

  & .dropdown-items {
    position: relative;
  }

  @media (min-width: 768px) and (max-width: 991px) {
    margin-top: 7.5px;
  }
`;

type Props = {
  className?: string;
  price: number;
  purchased: boolean;
  gallery: Gallery;
};

export default function GalleryPurchasePanel(props: Props) {
  const { className, price, purchased, gallery } = props;

  const dispatch = useDispatch();

  const site = useSelector((state) => state.site);
  const {
    contentCdnUrl,
    ssoUrl,
    config: { hasPPS },
  } = useSelector((state) => state.app);
  const ppsInfo = useSelector((state) => state.PPSInfo);
  const user = useSelector((state) => state.auth.user);

  const [cookies] = useCookies([MAM_COOKIE_NAME, `${site.abbreviation}_PA`]);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const intl = useIntl();
  const getGalleryCover = useGetGalleryCoverUrl();

  const mamPartnerID = useMemo(() => (cookies.mam ? cookies.mam.split('.')[0] : null), [cookies.mam]);
  const affiliateID = cookies[`${site.abbreviation}_PA`];

  const affiliateCode = useMemo(() => {
    if (mamPartnerID) {
      return `mam-${mamPartnerID}`;
    }

    return affiliateID;
  }, [mamPartnerID, affiliateID]);

  const togglePurchase = useCallback(async () => {
    // need to check the inverse since that is what will be set in state
    if (!showPurchaseModal) {
      const product = generatePurchaseProduct(gallery.UUID, 'high', site.UUID);

      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          affiliate_id: affiliateCode,
          source: 'PPS Dialog',
          currency: product?.currency,
          items: [
            {
              item_name: gallery.name,
              item_brand: site.name,
              item_category: 'pps',
              item_category2: 'High',
              item_list_name: 'Galleries',
              item_list_id: product?.objectUUID,
              quantity: 1,
              price: product?.initialPrice,
            },
          ],
        },
      });
      window.dataLayer.push({
        event: 'select_item_ua',
        ecommerce: {
          currency: product?.currency,
          click: {
            actionField: {
              affiliate_id: affiliateCode,
              source: 'PPS Dialog',
              category2: 'High',
            },
            products: [
              {
                id: product?.objectUUID,
                name: gallery.name,
                brand: site.name,
                category: 'pps',
                variant: 'Galleries',
                quantity: 1,
                price: product?.initialPrice,
              },
            ],
          },
        },
      });
    }

    setShowPurchaseModal(!showPurchaseModal);
  }, [affiliateCode, gallery.name, gallery.UUID, showPurchaseModal, site.name, site.UUID]);

  const chargeByPreviousPurchase = useCallback(async () => {
    setPurchasing(true);

    const params = {
      affiliateCode,
      galleryName: gallery.name,
      galleryUUID: gallery.UUID,
      isStaffSelection: gallery.isStaffSelection,
      quality: 'high',
      siteName: site.name,
      siteUUID: site.UUID,
      type: 'gallery',
    };

    dispatch(cbpPurchase(params));
  }, [affiliateCode, dispatch, gallery.isStaffSelection, gallery.name, gallery.UUID, site.name, site.UUID]);

  const purchaseScene = useCallback(
    async ({ email, password }) => {
      setPurchasing(true);

      const data = {
        email,
        galleryUUID: gallery.UUID,
        password,
        quality: 'high',
        siteUUID: gallery.siteUUID,
      };

      try {
        const api = new ApiClient();
        const purchaseEndpoint = constructUrl(urls.post.purchase.scene);

        const {
          body: { billerID, cartItem, hash, url },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } = await (api as any).post(purchaseEndpoint, { data });

        const iframeURL = cartItem.iframeURL.trim();
        const redirectURL = cartItem.redirectURL.trim();

        window.dataLayer.push({
          event: 'begin_checkout',
          ecommerce: {
            affiliate_id: affiliateCode,
            source: 'PPS Dialog',
            value: cartItem.product.initialPrice,
            tax: '',
            currency: cartItem.product.currency,
            payment_method: BillerById[billerID],
            cbp: 0,
            items: [
              {
                item_name: gallery.name,
                item_id: cartItem.product.productUUID,
                item_brand: site.name,
                item_category: 'pps',
                item_category2: 'High',
                item_list_name: 'Galleries',
                item_list_id: addDashesToUUID(gallery.UUID),
                quantity: 1,
                price: cartItem.product.initialPrice,
              },
            ],
          },
        });
        window.dataLayer.push({
          event: 'begin_checkout_ua',
          ecommerce: {
            currency: cartItem.product.currency,
            checkout: {
              actionField: {
                category2: 'High',
                list_name: 'Galleries',
                option: BillerById[billerID],
                cbp: 0,
                affiliate_id: affiliateCode,
                source: 'PPS Dialog',
                revenue: cartItem.product.initialPrice,
                tax: '',
                product_uuid: cartItem.product.productUUID,
              },
              products: [
                {
                  name: gallery.name,
                  id: addDashesToUUID(gallery.UUID),
                  brand: site.name,
                  category: 'pps',
                  quantity: 1,
                  price: cartItem.product.initialPrice,
                },
              ],
            },
          },
        });

        window.location.href = `${ssoUrl}/login/${hash}?continue=${url}&cartItemUUID=${cartItem.cartItemUUID}&${
          iframeURL ? `iframeURL=${encodeURIComponent(iframeURL)}` : `redirectURL=${encodeURIComponent(redirectURL)}`
        }`;
      } catch (err: unknown) {
        const error = err as ResponseError;
        setPurchasing(false);
        let message = 'Submission failed, please try again later';

        if (error?.response?.status === 409) {
          message = 'Please login to complete this purchase';
        }

        throw new SubmissionError({ email: message } as FormErrors<{
          email: string;
        }>);
      }
    },
    [affiliateCode, gallery.name, gallery.siteUUID, gallery.UUID, setPurchasing, site.name, ssoUrl],
  );

  const showDownloads = !purchased && hasPPS && !props.gallery.downloadsDisabled;

  return (
    <PanelWrapper className={className}>
      <PurchasePanel>
        <div className="headline">
          {intl.formatMessage({
            id: 'galleryPurchasePanel.headline',
            defaultMessage: 'THE HIGHEST QUALITY OF EXCLUSIVE CONTENT',
          })}
        </div>
        <PurchaseButton
          topTitle={intl.formatMessage({
            id: 'gallery.buttons.purchaseButton.topTitle',
            defaultMessage: 'Access All Galleries Instantly',
          })}
          mainTitle={intl.formatMessage({
            id: 'gallery.buttons.purchaseButton.mainTitle',
            defaultMessage: 'Get Full Site Access',
          })}
        />
        {showDownloads && (
          <>
            <div>
              <small>
                {intl.formatMessage({ id: 'galleryPurchasePanel.alsoAvailable', defaultMessage: 'Also available' })}
              </small>
            </div>
            <a className="btn inline-block btn btn-download" onClick={togglePurchase}>
              <span className="icon">
                <FontAwesomeIcon icon={faDownload} />
              </span>
              <span className="no-dropdown">
                {intl.formatMessage(
                  { id: 'gallery.buttons.download', defaultMessage: `Download at $${price}` },
                  { price: `$${price}` },
                )}
              </span>
            </a>
          </>
        )}
      </PurchasePanel>
      <PurchaseGalleryDialog
        onHide={togglePurchase}
        price={price}
        purchaseError={ppsInfo.purchaseError}
        purchaseLoading={ppsInfo.purchaseLoading}
        purchaseRedirect={ppsInfo.purchaseRedirect}
        purchaseScene={user && user.email ? chargeByPreviousPurchase : purchaseScene}
        purchaseStatus={ppsInfo.purchaseStatus}
        quality="High"
        show={showPurchaseModal}
        submitted={purchasing}
        thumbnail={getGalleryCover(contentCdnUrl, gallery, { thumbnail: true })}
        wideThumbnail={getGalleryCover(contentCdnUrl, gallery, { thumbnail: true })}
        title={gallery.name}
        type="gallery"
        user={!!(user && user.email)}
        year={new Date(gallery.publishedAt).getUTCFullYear()}
        description={gallery.description}
        validating={ppsInfo.validating}
      />
    </PanelWrapper>
  );
}

const PurchasePanel = styled(Panel)`
  margin-bottom: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
